import { apiClient } from "@/lib/api";
import type {
  CreateVehiclePayload,
  PaginatedVehicles,
  UpdateVehiclePayload,
  Vehicle,
} from "@/types/vehicles";

export interface GetVehiclesParams {
  page?: number;
  page_size?: number;
  search?: string;
  is_active?: boolean;
  /** Filter by speed_profile enum: car | bike | foot */
  speed_profile?: string;
  /** Filter by assigned driver ID */
  driver_id?: number;
  /**
   * Fleet chip (tracker#45): `on_route` | `free` | `inactive` | `needs_attention`.
   *
   * Separate from `is_active`, which the chips replaced: `inactive` is one chip among five,
   * and the backend's occupancy rules (a planned or active route today, the same definition
   * the route builder uses) cannot be expressed through a boolean.
   */
  fleet_status?: string;
  /** Ordering field, prefix with '-' for descending; e.g. 'payload_kg' or '-volume_m3' */
  ordering?: string;
}

/**
 * Fetches a paginated list of vehicles.
 * GET /api/v1/vehicles/
 */
export async function getVehicles(
  params?: GetVehiclesParams,
): Promise<PaginatedVehicles> {
  const response = await apiClient.get<PaginatedVehicles>("/v1/vehicles/", {
    params,
  });
  return response.data;
}

/**
 * Fetches a single vehicle by ID.
 * GET /api/v1/vehicles/{id}/
 */
export async function getVehicle(id: number): Promise<Vehicle> {
  const response = await apiClient.get<Vehicle>(`/v1/vehicles/${id}/`);
  return response.data;
}

/**
 * Response of `GET /api/v1/vehicles/available-count/`.
 *
 * The body is `{"count": N}` and nothing else — verified against the endpoint
 * (`apps/vehicles/views.py::available_count`), which does NOT echo the date
 * back. Declaring a `date` field here would be a type that lies: it would
 * compile, then read `undefined` at runtime.
 */
export interface VehicleAvailableCount {
  /** How many vehicles the route builder may place a new route on that date. */
  count: number;
}

/**
 * How many vehicles are AVAILABLE on `date` — active, in this tenant, and not
 * already holding a `planned`/`active` route that day.
 *
 * TASK-4827. This figure MUST come from the server: the only truthful
 * definition lives in `apps.vehicles.services.available_vehicles_for_date`,
 * which the auto-builder itself calls, so the number the modal advertises and
 * the number the builder considers cannot drift.
 *
 * Deriving it on the client is forbidden and was ruled out explicitly. Counting
 * `GET /v1/vehicles/?is_active=true` OVERSTATES availability (it includes
 * vehicles already booked that day), and counting the loaded combobox page is
 * worse still — that list is paginated at 20 and narrowed by the search box, so
 * it would present a page-local number as a fleet-wide fact. That is the
 * BUG-274..278 defect class.
 *
 * Waves (BUG-672): with `waveStart` the server also counts vans whose planned
 * route that day ends at or before it — the rule the auto build applies to a
 * build whose earliest order window opens at `waveStart`. Omitted, the count
 * keeps the standing definition above.
 *
 * @param date ISO `YYYY-MM-DD`.
 * @param waveStart Tenant-local `HH:MM`; omit for no wave.
 */
export async function getAvailableVehicleCount(
  date: string,
  waveStart?: string,
): Promise<VehicleAvailableCount> {
  const response = await apiClient.get<VehicleAvailableCount>(
    "/v1/vehicles/available-count/",
    {
      params:
        waveStart === undefined ? { date } : { date, wave_start: waveStart },
    },
  );
  return response.data;
}

/**
 * Creates a new vehicle.
 * POST /api/v1/vehicles/
 */
export async function createVehicle(
  payload: CreateVehiclePayload,
): Promise<Vehicle> {
  const response = await apiClient.post<Vehicle>("/v1/vehicles/", payload);
  return response.data;
}

/**
 * Partially updates an existing vehicle.
 * PATCH /api/v1/vehicles/{id}/
 */
export async function updateVehicle(
  id: number,
  payload: UpdateVehiclePayload,
): Promise<Vehicle> {
  const response = await apiClient.patch<Vehicle>(
    `/v1/vehicles/${id}/`,
    payload,
  );
  return response.data;
}

/**
 * Deletes a vehicle by ID.
 * DELETE /api/v1/vehicles/{id}/
 */
export async function deleteVehicle(id: number): Promise<void> {
  await apiClient.delete(`/v1/vehicles/${id}/`);
}
